/**
 * Leegstand Tour
 *
 * Step-by-step walkthrough of the leegstand workflow: from Whise publication
 * on Immoweb → linking the reference → keeping the appointment calendar
 * online → checking advertised prices → watching candidates come in.
 *
 * Every step anchors to a real UI element via `data-tour` attributes.
 */

import { TourDefinition } from "../config/tourSteps";

export const leegstandTour: TourDefinition = {
  id: "leegstandTour",
  steps: [
    {
      element: '[data-tour="vacant-table"]',
      popover: {
        title: "Publicatie via Whise",
        description:
          "Als een unit leeg komt moet deze eerst via Whise op Immoweb worden gepubliceerd. De knop rechtsboven opent Whise in een nieuw tabblad.",
        side: "bottom",
        align: "start",
      },
    },
    {
      element: '[data-tour="vacant-unit-id"]',
      popover: {
        title: "Unit detailpagina",
        description:
          "Klik op de unit-id om de detailpagina in een nieuw tabblad te openen. Daar zie je welke info nog ontbreekt vooraleer je de unit kan publiceren.",
        side: "right",
        align: "start",
      },
    },
    {
      element: '[data-tour="vacant-leeg-sinds"]',
      popover: {
        title: "Leeg sinds",
        description:
          "Hoe lang de unit al leeg staat. De teller start automatisch vanaf oplevering renovatie, einde van het laatste contract of aankoopdatum. Hover over het potlood om de datum handmatig aan te passen.",
        side: "bottom",
      },
    },
    {
      element: '[data-tour="vacant-gederfde-huur"]',
      popover: {
        title: "Gederfde huur",
        description:
          "Hoeveel huur we al zijn misgelopen. Formule: ERV × (dagen leeg ÷ 30,44). Het vraagteken naast de kolomtitel toont de volledige berekening.",
        side: "bottom",
      },
    },
    {
      element: '[data-tour="vacant-kandidaten"]',
      popover: {
        title: "Kandidaten",
        description:
          "Aantal actieve kandidaten voor deze unit. Klik om meteen naar de kandidaten-pagina te gaan, gefilterd op dit pand, zodat je ze kan opvolgen.",
        side: "bottom",
      },
    },
    {
      element: '[data-tour="vacant-kalender"]',
      popover: {
        title: "Afsprakenkalender online",
        description:
          "Zorg dat er steeds afspraakmogelijkheden openstaan én dat de afsprakenkalender op groen staat. Vergeet niet om de kalender offline te halen zodra de unit verhuurd is. Klik op het kalender-icoon om tijdslots te beheren.",
        side: "bottom",
      },
    },
    {
      element: '[data-tour="vacant-publicatie"]',
      popover: {
        title: "Immoweb referentie koppelen",
        description:
          "Geef hier de Immoweb-referentie in zodra de unit op Immoweb gepubliceerd is. Zolang de referentie ingevuld staat, leest Ella automatisch de inkomende mails uit die deze referentie bevatten. Belangrijk: laat Immoweb-mails altijd op 'ongelezen' staan en verwerk ze niet manueel — het systeem markeert ze zelf als gelezen en archiveert ze daarna. De Yally-toggle zet het pand ook op yally.apartments.",
        side: "left",
      },
    },
    {
      element: '[data-tour="vacant-makelaar"]',
      popover: {
        title: "Makelaars",
        description:
          "Indien een externe makelaar de verhuur (mee) opvolgt, koppel hier het contact of de contacten. Via de kandidaten-pagina kan je hun contactgegevens doorsturen naar deze makelaars met één klik.",
        side: "left",
      },
    },
    {
      element: '[data-tour="vacant-bezichtigings"]',
      popover: {
        title: "Bezichtigingsverantwoordelijke",
        description:
          "De persoon die elke bezichtiging effectief uitvoert. Deze krijgt automatisch alle e-mails van kandidaten die een afspraak boeken, plus een agenda-uitnodiging per bezoekmoment. Typisch iemand van Yally, maar kan ook een externe partner zijn.",
        side: "left",
      },
    },
    {
      element: '[data-tour="vacant-geafficheerde-huur"]',
      popover: {
        title: "Geafficheerde huur vs. ERV",
        description:
          "Controleer de geafficheerde huur en provisies — daar kunnen we vaak een huurverhoging realiseren. Een groene bolletje betekent dat we boven ERV vragen, oranje dat we eronder zitten. Hover voor details.",
        side: "bottom",
      },
    },
    {
      element: '[data-tour="vacant-erv"]',
      popover: {
        title: "ERV — Estimated Rental Value",
        description:
          "Dit is de interne schatting van wat we denken te halen. Niet per se gelijk aan de geafficheerde huur. Je kan de waarde rechtstreeks hier aanpassen.",
        side: "bottom",
      },
    },
  ],
};
